// src/utils/contratoOportunidade.ts
//
// Regra única de criação de contrato a partir da edição de uma oportunidade.
// Usada pelo Kanban (OpportunityFormHandler) e pela tela de detalhes
// (useOpportunityDetailHandler), para que os dois caminhos nunca divirjam.
import { apiService } from '../services/api';
import { CreateOpportunityData } from '../types/opportunity';

export interface ResultadoCriacaoContrato {
    // criado: contrato novo | ja_existe: já havia contrato para a oportunidade | erro: falhou
    status: 'criado' | 'ja_existe' | 'erro';
    idContrato?: number;
    mensagem?: string;
}

/**
 * Cria o contrato da oportunidade a partir dos dados do formulário de edição.
 * Deve ser chamada DEPOIS de a oportunidade ter sido atualizada com sucesso.
 * Nunca lança erro: o resultado indica o que aconteceu.
 */
export const criarContratoDaOportunidade = async (
    opportunityId: number,
    dados: CreateOpportunityData
): Promise<ResultadoCriacaoContrato> => {
    try {
        // Evita contrato duplicado ao salvar de novo uma oportunidade já "Ganha"
        try {
            const existentes = await apiService.getContratos({ id_oportunidade: opportunityId });
            if (existentes.success && Array.isArray(existentes.data) && existentes.data.length > 0) {
                return { status: 'ja_existe', idContrato: existentes.data[0].id };
            }
        } catch (checkError) {
            // Se a checagem falhar, segue para a criação (ela própria acusa se algo estiver errado)
            console.error('Erro ao verificar contrato existente:', checkError);
        }

        let dataInicio: string;
        let dataFim: string;

        // Usar datas fornecidas ou calcular padrão (1 ano)
        if (dados.contrato_data_inicio && dados.contrato_data_fim) {
            // Datas customizadas do modal
            dataInicio = dados.contrato_data_inicio;
            dataFim = dados.contrato_data_fim;
        } else {
            // Padrão: 1 ano a partir de hoje
            const hoje = new Date();
            const daquiUmAno = new Date(hoje);
            daquiUmAno.setFullYear(daquiUmAno.getFullYear() + 1);

            dataInicio = hoje.toISOString().split('T')[0];
            dataFim = daquiUmAno.toISOString().split('T')[0];
        }

        // Prioriza o valor da proposta; senão usa o valor da licitação
        const valorContrato = (dados.valor_total_proposta !== null &&
            dados.valor_total_proposta !== undefined &&
            dados.valor_total_proposta > 0)
            ? dados.valor_total_proposta
            : dados.valor_licitacao;

        // Mantido como variável (não literal): a API aceita campos além dos declarados em CreateContratoData
        const contratoData = {
            id_oportunidade: opportunityId,
            id_licitante: dados.id_contato,
            data_inicio: dataInicio,
            data_finalizacao: dataFim,
            prazo_vencimento: dados.contrato_prazo_vencimento,
            valor_total: valorContrato,
            numero_contrato: dados.numero_contrato,
            observacoes: dados.titulo || `Contrato gerado automaticamente a partir da oportunidade #${opportunityId}`,
            descricao: dados.titulo
        };

        const contratoResponse = await apiService.createContrato(contratoData);

        return { status: 'criado', idContrato: contratoResponse.data.id };
    } catch (error) {
        console.error('Erro ao criar contrato:', error);
        return {
            status: 'erro',
            mensagem: error instanceof Error ? error.message : 'Erro desconhecido ao criar contrato'
        };
    }
};

/**
 * Separa a flag de criação de contrato do restante do payload da oportunidade,
 * para que ela não seja enviada à API de atualização.
 */
export const separarCriarContrato = (
    dados: CreateOpportunityData
): { payload: CreateOpportunityData; criarContrato: boolean } => {
    const { criarContrato, ...payload } = dados as CreateOpportunityData & { criarContrato?: boolean };
    return { payload: payload as CreateOpportunityData, criarContrato: criarContrato === true };
};
